/**
 * A visitor's privacy signals, and the ONE decision every OrderUp-served site
 * makes from them: may a merchant's advertising / analytics tags load?
 *
 * ## Why this exists (SOC 2 register R39)
 *
 * OrderUp's privacy policy says a valid Global Privacy Control signal is
 * honoured as an opt-out of "sale" and "sharing". Nothing implemented it: a
 * restaurant site served by OrderUp injected the restaurant's own Meta Pixel,
 * Google Analytics 4 and Google Tag Manager for every visitor, GPC or not, and
 * offered no way to opt out.
 *
 * ## The rule
 *
 * A merchant marketing tag loads ONLY when the visitor has sent neither signal:
 *
 * - **Global Privacy Control** — `navigator.globalPrivacyControl === true`. A
 *   browser that sends the `Sec-GPC: 1` request header exposes the same value
 *   here (the GPC spec defines both), and no OrderUp page server-renders a
 *   merchant tag, so the client read is the single decision point.
 * - **"Your Privacy Choices" opt-out** — a first-party, host-only cookie
 *   ({@link SALE_SHARE_OPT_OUT_COOKIE}) the site's footer link writes. Same
 *   effect as GPC, on this site, in this browser.
 *
 * "Merchant marketing tags" means every third-party tag a restaurant configures
 * in `websiteConfig.analytics` — GTM (a container that can load anything), GA4
 * and the Meta Pixel. OrderUp's own first-party operational telemetry (the
 * cookieless pageview beacon, Vercel Analytics / Speed Insights, PostHog product
 * analytics, Sentry error reporting) is not decided here: it is not sold or
 * shared for advertising, so the opt-out does not reach it.
 *
 * Pure functions plus two thin browser readers; the React hook lives in
 * `usePrivacySignals.ts`.
 */

/** First-party cookie recording a "Do Not Sell or Share" choice on this site. */
export const SALE_SHARE_OPT_OUT_COOKIE = "ou_sale_share_opt_out";

/**
 * How long a recorded opt-out lasts (seconds): one year, after which the
 * browser drops it. 12 months is the shortest period the CCPA regulations let a
 * business wait before asking again; GPC, by contrast, is read on every visit.
 */
export const SALE_SHARE_OPT_OUT_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/** Window event fired when a choice is recorded, so mounted tags re-decide. */
export const PRIVACY_CHOICE_EVENT = "orderup:privacy-choice";

export interface PrivacySignals {
  /** The browser is sending Global Privacy Control. */
  readonly globalPrivacyControl: boolean;
  /** The visitor opted out through this site's "Your Privacy Choices" link. */
  readonly saleShareOptOut: boolean;
}

export type MerchantMarketingTagsDecision =
  | { readonly load: true; readonly reason: "no_opt_out_signal" }
  | {
      readonly load: false;
      readonly reason: "global_privacy_control" | "sale_share_opt_out";
    };

/**
 * THE decision. Either signal alone suppresses every merchant marketing tag;
 * GPC is named first because it needs no action on this site.
 */
export function decideMerchantMarketingTags(
  signals: PrivacySignals,
): MerchantMarketingTagsDecision {
  if (signals.globalPrivacyControl) {
    return { load: false, reason: "global_privacy_control" };
  }
  if (signals.saleShareOptOut) {
    return { load: false, reason: "sale_share_opt_out" };
  }
  return { load: true, reason: "no_opt_out_signal" };
}

/**
 * `navigator.globalPrivacyControl === true`, and nothing else counts: the spec
 * defines a boolean, and a truthy string from a polyfill is not a signal we can
 * attribute to the visitor.
 */
export function readGlobalPrivacyControl(navigatorLike: unknown): boolean {
  if (!navigatorLike || typeof navigatorLike !== "object") return false;
  return (navigatorLike as { globalPrivacyControl?: unknown }).globalPrivacyControl === true;
}

/** True when a `document.cookie` string carries the opt-out cookie set to `1`. */
export function readSaleShareOptOut(cookieString: string | null | undefined): boolean {
  if (!cookieString) return false;
  for (const part of cookieString.split(";")) {
    const eq = part.indexOf("=");
    if (eq === -1) continue;
    if (part.slice(0, eq).trim() !== SALE_SHARE_OPT_OUT_COOKIE) continue;
    if (part.slice(eq + 1).trim() === "1") return true;
  }
  return false;
}

/**
 * The `document.cookie` assignment that records (or clears) the opt-out.
 * Host-only (no `Domain`), so a choice on one restaurant's custom domain is that
 * site's alone; `Secure` whenever the page is https.
 */
export function serializeSaleShareOptOutCookie(
  optedOut: boolean,
  options: { readonly secure: boolean },
): string {
  const parts = [
    `${SALE_SHARE_OPT_OUT_COOKIE}=${optedOut ? "1" : ""}`,
    "Path=/",
    `Max-Age=${optedOut ? SALE_SHARE_OPT_OUT_MAX_AGE_SECONDS : 0}`,
    "SameSite=Lax",
  ];
  if (options.secure) parts.push("Secure");
  return parts.join("; ");
}

type BrowserLike = {
  readonly navigator?: unknown;
  readonly document?: { cookie: string };
  readonly location?: { readonly protocol?: string };
  dispatchEvent?: (event: Event) => boolean;
};

function currentBrowser(): BrowserLike | undefined {
  return typeof window === "undefined" ? undefined : (window as unknown as BrowserLike);
}

/**
 * Read both signals from the browser. Outside a browser (server render) there
 * is no visitor to read, and callers must not decide from this — the hook keeps
 * the decision `null` until it runs on the client.
 */
export function readPrivacySignals(browser: BrowserLike | undefined = currentBrowser()): PrivacySignals {
  let saleShareOptOut = false;
  try {
    saleShareOptOut = readSaleShareOptOut(browser?.document?.cookie);
  } catch {
    // A sandboxed frame or blocked storage throws on read. An opt-out can never
    // have been recorded where the cookie cannot be read (it stays false), and
    // GPC still applies.
  }
  return {
    globalPrivacyControl: readGlobalPrivacyControl(browser?.navigator),
    saleShareOptOut,
  };
}

/**
 * Record (or withdraw) the visitor's opt-out on this site, then READ IT BACK.
 * Returns whether the cookie now says what was asked — `false` means the browser
 * refused it (cookies blocked), and the caller must tell the visitor rather than
 * claim the choice was saved.
 */
export function recordSaleShareOptOut(
  optedOut: boolean,
  browser: BrowserLike | undefined = currentBrowser(),
): boolean {
  if (!browser?.document) return false;
  try {
    browser.document.cookie = serializeSaleShareOptOutCookie(optedOut, {
      secure: browser.location?.protocol === "https:",
    });
    const stored = readSaleShareOptOut(browser.document.cookie);
    if (stored !== optedOut) return false;
  } catch {
    return false;
  }
  try {
    if (typeof Event === "function") browser.dispatchEvent?.(new Event(PRIVACY_CHOICE_EVENT));
  } catch {
    // Notifying mounted tags is best-effort; the choice itself is stored.
  }
  return true;
}
